import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import {
  MagnifyingGlass, CalendarCheck, CalendarBlank, SquaresFour,
  ClockCounterClockwise, Tag, Palette, Plus, FileArrowDown, ChartLineUp,
} from "@phosphor-icons/react";
import { humanDate, formatTime } from "@/lib/date";

export default function CommandPalette({ open, onOpenChange, onQuickAdd }) {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ tasks: [], events: [], important: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!open) { setQuery(""); setResults({ tasks: [], events: [], important: [] }); setActiveIdx(0); }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setResults({ tasks: [], events: [], important: [] }); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`);
        if (!cancelled) setResults(data);
      } finally { if (!cancelled) setLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const NAV_ACTIONS = [
    { id: "n-today", label: "Ga naar Vandaag", icon: CalendarCheck, action: () => nav("/today") },
    { id: "n-week", label: "Ga naar Week", icon: SquaresFour, action: () => nav("/week") },
    { id: "n-month", label: "Ga naar Maand", icon: CalendarBlank, action: () => nav("/month") },
    { id: "n-routines", label: "Open Routines", icon: ClockCounterClockwise, action: () => nav("/routines") },
    { id: "n-categories", label: "Open Categorieën", icon: Tag, action: () => nav("/categories") },
    { id: "n-import", label: "Rooster importeren", icon: FileArrowDown, action: () => nav("/import") },
    { id: "n-recap", label: "Weekterugblik", icon: ChartLineUp, action: () => nav("/recap") },
    { id: "n-settings", label: "Weergave-instellingen", icon: Palette, action: () => nav("/settings") },
    { id: "a-quick", label: "Snel toevoegen…", icon: Plus, action: () => { onQuickAdd?.(); return true; } },
  ];

  const q = query.toLowerCase();
  const filteredNav = q ? NAV_ACTIONS.filter((n) => n.label.toLowerCase().includes(q)) : NAV_ACTIONS;
  const allResults = [
    ...filteredNav.map((n) => ({ type: "nav", ...n })),
    ...results.events.slice(0, 6).map((e) => ({ type: "event", id: `e-${e.id}`, label: e.title, sub: `${humanDate(e.date)} · ${formatTime(e.start_time)}`, icon: CalendarBlank, action: () => nav("/today") })),
    ...results.tasks.slice(0, 6).map((t) => ({ type: "task", id: `t-${t.id}`, label: t.title, sub: `Taak · ${t.date}`, icon: CalendarCheck, action: () => nav("/today") })),
    ...results.important.slice(0, 6).map((i) => ({ type: "important", id: `i-${i.id}`, label: i.title, sub: `${i.kind} · ${i.date}`, icon: CalendarBlank, action: () => nav("/month") })),
  ];

  useEffect(() => { setActiveIdx(0); }, [query, results]);

  const runAt = (idx) => {
    const it = allResults[idx];
    if (!it) return;
    const keepOpen = it.action();
    if (!keepOpen) onOpenChange(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, allResults.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); runAt(activeIdx); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121C2F] border-slate-800 text-slate-100 max-w-xl p-0 overflow-hidden" data-testid="command-palette">
        <DialogTitle className="sr-only">Opdrachtpalet</DialogTitle>
        <DialogDescription className="sr-only">Zoek in afspraken, taken en navigeer door de app.</DialogDescription>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
          <MagnifyingGlass size={18} className="text-slate-400" />
          <Input
            data-testid="command-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Zoek of typ een opdracht..."
            className="border-0 bg-transparent focus-visible:ring-0 text-slate-100 h-9 px-0 text-base"
          />
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-700 text-slate-500 font-mono">ESC</span>
        </div>
        <div className="max-h-[420px] overflow-y-auto py-2">
          {allResults.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              {loading ? "Bezig met zoeken..." : "Geen resultaten. Typ iets."}
            </div>
          )}
          {allResults.map((it, idx) => (
            <button
              key={it.id}
              data-testid={`cmd-item-${it.id}`}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => runAt(idx)}
              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                idx === activeIdx ? "bg-blue-600/15 text-slate-50" : "text-slate-300 hover:bg-slate-800/40"
              }`}
            >
              <it.icon size={16} className={idx === activeIdx ? "text-blue-400" : "text-slate-500"} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{it.label}</div>
                {it.sub && <div className="text-[11px] text-slate-500 truncate">{it.sub}</div>}
              </div>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">{it.type}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-slate-800 px-4 py-2 flex items-center justify-between text-[11px] text-slate-500">
          <span>↑↓ navigeer · ↵ kies</span>
          <span className="font-mono">⌘ K</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
